import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Sparkles, Send, AlertTriangle, TrendingUp, X } from "lucide-react";
import { cmdApi } from "../api";
import { usePermissions } from "../hooks/usePermissions";
import { formatINR } from "../format";
import { fmtPct } from "../fincalc";

const AUDIENCE = [
  { v: "board", l: "Board" },
  { v: "investors", l: "Investors" },
  { v: "ops", l: "Operations" },
];

function monthShift(month, delta) {
  if (!month) return month;
  const [y, m] = month.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}
function istLatestMonth() {
  const now = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
  const y = now.getFullYear(), m = now.getMonth();
  const last = new Date(Date.UTC(y, m - 1, 1));
  return `${last.getUTCFullYear()}-${String(last.getUTCMonth() + 1).padStart(2, "0")}`;
}

function Delta({ pct }) {
  if (pct === null || pct === undefined) return <span className="cmd-muted-cell">—</span>;
  const neg = pct < 0;
  return (
    <span className={neg ? "neg" : ""} style={{ fontWeight: 600 }}>
      {neg ? "▼" : "▲"} {Math.abs(pct * 100).toFixed(1)}%
    </span>
  );
}

function KpiCard({ label, value, sub, testId }) {
  return (
    <div className="cmd-kpi" data-testid={testId}>
      <div className="cmd-kpi-value" style={{ fontSize: 22 }}>{value}</div>
      <div className="cmd-kpi-label">{label}</div>
      {sub && <div className="cmd-kpi-sub">{sub}</div>}
    </div>
  );
}

export default function CmdInsights() {
  const { verticals } = usePermissions();
  const [to, setTo] = useState(istLatestMonth());
  const [from, setFrom] = useState(monthShift(istLatestMonth(), -5));
  const [scope, setScope] = useState("all");
  const [audience, setAudience] = useState("board");
  const [overview, setOverview] = useState(null);
  const [brief, setBrief] = useState(null);
  const [briefBusy, setBriefBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [question, setQuestion] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const chatRef = useRef(null);

  const loadOverview = async () => {
    try {
      const { data } = await cmdApi.get(`/insights/overview?from=${from}&to=${to}&scope=${encodeURIComponent(scope)}`);
      setOverview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load insights.");
      setOverview(null);
    }
  };
  useEffect(() => { if (from && to && from <= to) loadOverview(); }, [from, to, scope]); // eslint-disable-line

  const generateBrief = async () => {
    setBriefBusy(true); setBrief(null);
    try {
      const { data } = await cmdApi.post("/insights/brief", {
        date_from: from, date_to: to, scope, audience,
      });
      setBrief(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Brief generation failed.");
    } finally { setBriefBusy(false); }
  };

  const askQuestion = async () => {
    const q = question.trim();
    if (!q) return;
    setChatBusy(true);
    setHistory((h) => [...h, { role: "user", text: q }]);
    setQuestion("");
    try {
      const { data } = await cmdApi.post("/insights/chat", {
        date_from: from, date_to: to, scope, question: q,
        session_id: sessionId,
      });
      if (data.session_id) setSessionId(data.session_id);
      setHistory((h) => [...h, { role: "ai", text: data.answer }]);
    } catch (e) {
      setHistory((h) => [...h, { role: "ai", text: e?.response?.data?.detail || "Couldn't answer right now." }]);
    } finally { setChatBusy(false); setTimeout(() => { chatRef.current?.scrollTo(0, 1e9); }, 50); }
  };

  const cmp = overview?.comparisons;
  const rt = overview?.range_total;
  const sortedTrend = useMemo(() => (overview?.per_month || []), [overview]);

  return (
    <section data-testid="cmd-insights">
      <div className="cmd-toolbar" style={{ alignItems: "center" }}>
        <label className="cmd-muted" style={{ fontSize: 12 }}>From</label>
        <input type="month" className="cmd-input cmd-filter" value={from} max={to}
          onChange={(e) => setFrom(e.target.value)} data-testid="insights-from" />
        <label className="cmd-muted" style={{ fontSize: 12 }}>To</label>
        <input type="month" className="cmd-input cmd-filter" value={to} min={from}
          onChange={(e) => setTo(e.target.value)} data-testid="insights-to" />
        <select className="cmd-input cmd-filter" value={scope} onChange={(e) => setScope(e.target.value)}
          data-testid="insights-scope">
          <option value="all">Consolidated</option>
          {(verticals && verticals.length ? verticals : ["Coffee","Dairy","Hospitality","Retail food","Sri Lanka","Technology"]).map(
            (v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select className="cmd-input cmd-filter" value={audience}
            onChange={(e) => setAudience(e.target.value)} data-testid="insights-audience">
            {AUDIENCE.map((a) => <option key={a.v} value={a.v}>{a.l} brief</option>)}
          </select>
          <button className="cmd-btn cmd-btn-primary" onClick={generateBrief}
            disabled={briefBusy || !overview} data-testid="insights-generate">
            <Sparkles size={14} style={{ marginRight: 6 }} />
            {briefBusy ? "Thinking…" : "Generate AI brief"}
          </button>
        </div>
      </div>

      {!overview ? (
        <p className="cmd-muted" style={{ padding: 20 }}>Loading insights…</p>
      ) : (
        <>
          <div className="cmd-kpis4" style={{ marginBottom: 14 }}>
            <KpiCard label={`Revenue · ${overview.label}`} value={formatINR(rt.revenue)} testId="ins-kpi-revenue"
              sub={<span>vs last month <Delta pct={cmp.mom.revenue.pct_change} /> · vs last year <Delta pct={cmp.yoy.revenue.pct_change} /></span>} />
            <KpiCard label="EBITDA (range)" value={formatINR(rt.ebitda)} testId="ins-kpi-ebitda"
              sub={<span>vs last month <Delta pct={cmp.mom.ebitda.pct_change} /> · vs last year <Delta pct={cmp.yoy.ebitda.pct_change} /></span>} />
            <KpiCard label={`${cmp.fytd.fy} to date vs plan`} value={fmtPct(cmp.fytd.revenue_vs_plan)}
              testId="ins-kpi-fytd" sub={<span>Revenue {formatINR(cmp.fytd.revenue)} · EBITDA {formatINR(cmp.fytd.ebitda)}</span>} />
            <KpiCard label="Cash runway (months)" value={overview.cash_runway_months === null ? "Self-funding" : overview.cash_runway_months?.toFixed(1)}
              testId="ins-kpi-runway" sub={overview.cash_runway_months === null ? "EBITDA ≥ 0 on last 3 months" : "If current burn continues"} />
          </div>

          {overview.anomalies.length > 0 && (
            <div className="cmd-raised" style={{ padding: 14, borderRadius: 12, marginBottom: 14,
                                                borderLeft: "4px solid var(--cmd-health-watch)" }}
                 data-testid="ins-anomalies">
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, fontWeight: 600 }}>
                <AlertTriangle size={16} /> Anomalies detected ({overview.latest_month})
              </div>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.7 }}>
                {overview.anomalies.map((a, i) => (
                  <li key={i}><strong>{a.metric.replace("_", " ")}</strong>: {a.message}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="cmd-chart-wrap" data-testid="ins-trend">
            <h3>Revenue vs EBITDA · {overview.label}</h3>
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={sortedTrend} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--cmd-line)" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "var(--cmd-muted)" }}
                  tickFormatter={(m) => m.slice(2)} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "var(--cmd-muted)" }} axisLine={false} tickLine={false}
                  tickFormatter={(v) => formatINR(v)} width={70} />
                <Tooltip formatter={(v, name) => [formatINR(v), name === "revenue" ? "Revenue" : "EBITDA"]}
                  contentStyle={{ background: "var(--cmd-raised)", border: "1px solid var(--cmd-line)", borderRadius: 10, fontSize: 13 }} />
                <Bar dataKey="revenue" fill="var(--cmd-accent)" radius={[4, 4, 0, 0]} maxBarSize={26} />
                <Line type="monotone" dataKey="ebitda" stroke="var(--cmd-ink)" strokeWidth={2} connectNulls />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="cmd-ins-grid">
            <div className="cmd-raised cmd-ins-panel" data-testid="ins-vertical-table">
              <h3 style={{ margin: "0 0 10px", fontSize: 14, fontWeight: 600 }}>By vertical</h3>
              <div className="cmd-table-wrap">
                <table className="cmd-pl" style={{ minWidth: 420 }}>
                  <thead><tr><th>Vertical</th><th>Revenue</th><th>Share</th><th>GM %</th><th>EBITDA</th></tr></thead>
                  <tbody>
                    {overview.by_vertical.map((v) => (
                      <tr key={v.vertical} data-testid={`ins-vrow-${v.vertical.replace(/\s/g, "-")}`}>
                        <td>{v.vertical}</td>
                        <td>{v.revenue !== null ? formatINR(v.revenue) : "—"}</td>
                        <td>{v.share !== null && v.share !== undefined ? `${(v.share * 100).toFixed(1)}%` : "—"}</td>
                        <td>{v.gm_pct !== null ? fmtPct(v.gm_pct) : "—"}</td>
                        <td className={v.ebitda < 0 ? "neg" : ""}>{v.ebitda !== null ? formatINR(v.ebitda) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="cmd-raised cmd-ins-panel" data-testid="ins-portfolio">
              <h3 style={{ margin: "0 0 10px", fontSize: 14, fontWeight: 600 }}>Portfolio pulse</h3>
              <div className="cmd-sub2">
                <div><TrendingUp size={14} style={{ verticalAlign: "-2px" }} /> Open actions: <strong>{overview.portfolio.open_actions_total}</strong></div>
                <div>Decisions open: <strong>{overview.portfolio.decision_velocity.open}</strong>
                  · closed: <strong>{overview.portfolio.decision_velocity.closed_total}</strong>
                  · median close: <strong>{overview.portfolio.decision_velocity.median_days_to_close ?? "—"} d</strong></div>
              </div>
              <div style={{ marginTop: 10, fontSize: 13 }}>
                <div className="cmd-aging-bar">
                  {["0-7d", "8-30d", "31-90d", "90d+"].map((k) => (
                    <div key={k} className="cmd-aging-chip" data-testid={`aging-${k}`}>
                      <span>{k}</span><strong>{overview.portfolio.aging_actions[k]}</strong>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ marginTop: 12 }}>
                <h4 style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 600 }}>Health trends</h4>
                <ul className="cmd-health-trend-list">
                  {overview.portfolio.health_trends.slice(0, 8).map((h) => (
                    <li key={h.initiative_id}>
                      <span className="cmd-ins-init-name">{h.name}</span>
                      <span className="cmd-ins-trend-dots">
                        {(h.trend.length ? h.trend : [h.current]).map((t, i) => (
                          <i key={i} className={`cmd-health-dot ${t}`} title={t} />
                        ))}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {brief && (
            <div className="cmd-raised" style={{ padding: 18, borderRadius: 14, marginTop: 18 }}
                 data-testid="ins-brief">
              <h3 style={{ margin: "0 0 8px", fontSize: 16, fontWeight: 600 }}>
                {brief.headline || "Executive brief"}
                <span className="cmd-chip" style={{ marginLeft: 10, fontSize: 11 }}>AI · {audience}</span>
              </h3>
              <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6, fontSize: 14 }} data-testid="ins-brief-summary">
                {brief.summary}
              </div>
              <div className="cmd-ins-cols">
                <div data-testid="ins-brief-risks">
                  <h4><AlertTriangle size={14} /> Risks</h4>
                  <ul>{brief.risks.map((r, i) => <li key={i}>{r}</li>)}</ul>
                </div>
                <div data-testid="ins-brief-opps">
                  <h4><TrendingUp size={14} /> Opportunities</h4>
                  <ul>{brief.opportunities.map((r, i) => <li key={i}>{r}</li>)}</ul>
                </div>
              </div>
              {brief.by_vertical.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <h4 style={{ margin: "0 0 6px", fontSize: 13, fontWeight: 600 }}>By vertical</h4>
                  <div className="cmd-ins-vgrid">
                    {brief.by_vertical.map((v, i) => (
                      <div key={i} className="cmd-ins-vnote">
                        <div className="cmd-ins-vhead">{v.vertical}</div>
                        <div>{v.note}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="cmd-raised" style={{ padding: 18, borderRadius: 14, marginTop: 18 }}
               data-testid="ins-chat">
            <h3 style={{ margin: "0 0 10px", fontSize: 15, fontWeight: 600 }}>Ask anything — grounded on this range</h3>
            <div ref={chatRef} className="cmd-chat-log" data-testid="ins-chat-log">
              {history.length === 0 && (
                <p className="cmd-muted" style={{ fontSize: 13 }}>
                  Try: "Why did EBITDA drop in {overview.latest_month}?" or "Which vertical should we double down on?"
                </p>
              )}
              {history.map((m, i) => (
                <div key={i} className={`cmd-chat-msg ${m.role}`}>
                  <div className="cmd-chat-role">{m.role === "user" ? "You" : "Analyst AI"}</div>
                  <div style={{ whiteSpace: "pre-wrap" }}>{m.text}</div>
                </div>
              ))}
              {chatBusy && <div className="cmd-chat-msg ai"><div className="cmd-chat-role">Analyst AI</div><div>Thinking…</div></div>}
            </div>
            <div className="cmd-chat-input">
              <input className="cmd-input" placeholder="Ask about this range…" value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !chatBusy) askQuestion(); }}
                data-testid="ins-chat-q" />
              <button className="cmd-btn cmd-btn-primary" onClick={askQuestion}
                disabled={chatBusy || !question.trim()} data-testid="ins-chat-send">
                <Send size={14} /> Ask
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
